'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The Toothbrush Study Everyone Quotes Says the Opposite"
      author="MicroPlastics Research Desk"
      date="August 22, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/health.webp"
      heroImageAlt="Toothbrush bristles and microplastics: what the 2025 Jilin University study actually measured, and why bamboo toothbrush bristles are bio-based nylon rather than petroleum nylon"
    >
      <BlogLastUpdated iso="2026-08-22" formatted="August 22, 2026" />

      <BlogTLDR>
        <strong>There are two confident answers circulating about toothbrushes
        and microplastics, and the primary sources contradict both of
        them.</strong>{' '}
        The alarming one — <em>&ldquo;2.3 million microplastic particles a year
        straight into your mouth&rdquo;</em> — traces to a real 2025 paper from
        Jilin University. That paper says, in its own words, that the products
        tested <strong>&ldquo;do not directly contribute to the health risks
        associated with exposure to MPs&rdquo;</strong>, and its numbers describe
        release <strong>into the environment</strong>, not into you. It also
        found the leading toothbrush polymer was{' '}
        <strong>polypropylene</strong>, not the nylon everyone attributes to it.
        The debunking answer — <em>&ldquo;bamboo toothbrushes are a scam, the
        bristles are petroleum nylon&rdquo;</em> — is contradicted by the
        manufacturers themselves: Brush with Bamboo states its bristles are{' '}
        <strong>100% castor bean oil, USDA-certified biobased, with 0% fossil
        fuel and 0% petroleum</strong>. That claim is true.{' '}
        <strong>The accurate answer is duller and more useful: castor bristles
        are nylon-11, a genuine polyamide made from a plant.</strong> Bio-based
        describes what went in, not what comes out — and neither nylon-11 nor
        PA1010 is home-compostable or meaningfully biodegradable. The bamboo
        brush removes the petroleum and the plastic handle. It does not remove
        the synthetic polymer touching your teeth.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>The viral figure is a misquote of a real study.</strong> Wang et al. (2025) reported an average of at least 23.3 × 10⁵ — about 2.33 million — particles per person per year from toothbrushes, framed as environmental release.</>,
          <><strong>The same paper reached the opposite conclusion on health</strong>, stating that the toothbrushes and toothpastes tested do not directly contribute to microplastic health risks.</>,
          <><strong>It measured polypropylene, not nylon.</strong> The toothbrush simulation found polypropylene as the leading polymer at 22%, with 63% of particles under 100 µm.</>,
          <><strong>Toothpaste released fewer particles than the brush</strong> — up to 11.83 × 10⁵ per person per year, against at least 23.3 × 10⁵ from the toothbrush.</>,
          <><strong>Hard bristles shed fastest early on.</strong> Release climbed rapidly at first and then tapered, which is an argument for softer bristles and for replacing on schedule rather than stretching a brush.</>,
          <><strong>&ldquo;Petroleum nylon in bamboo brushes&rdquo; is not universally true.</strong> Brush with Bamboo publishes castor-bean bristles at 0% petroleum, and that is a first-party claim about feedstock.</>,
          <><strong>Castor bristles are still nylon.</strong> They are nylon-11 (PA11, sold as Rilsan) or PA1010 — plant-derived polyamides that are neither home-compostable nor meaningfully biodegradable.</>,
          <><strong>Boar bristle is the only mainstream fully biodegradable option</strong>, because it is animal keratin rather than any polymer. It is not for everyone, and it is not a microplastics emergency that forces the switch.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and what each one actually describes"
        stats={[
          { value: '23.3 × 10⁵', label: 'particles per person per year from toothbrushes', context: 'the paper’s own figure — about 2.33 million, framed as release into the environment, not ingestion', source: 'Wang et al., Ecotoxicology and Environmental Safety, 2025', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/40680448/' },
          { value: '22%', label: 'polypropylene — the leading toothbrush polymer found', context: 'not nylon, which is what most coverage of this study attributes to it', source: 'Wang et al., 2025', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/40680448/' },
          { value: '63%', label: 'of released particles under 100 µm', context: 'the size fraction that dominates; smaller particles are harder to measure and harder to dismiss', source: 'Wang et al., 2025', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/40680448/' },
          { value: '0%', label: 'petroleum in Brush with Bamboo’s bristles, per the manufacturer', context: 'castor bean oil, USDA-certified 100% biobased — a claim about feedstock, not about whether it is a polymer', source: 'Brush with Bamboo FAQ (manufacturer)', sourceUrl: 'https://brushwithbamboo.com/faq/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        Yes, toothbrush bristles shed. They are polymer filaments dragged across
        enamel twice a day under pressure, and abrasion is what makes plastics
        shed. That much is not in dispute and never was.
      </p>
      <p>
        What is in dispute is everything built on top of it. The number being
        quoted at you has been detached from the study that produced it, and the
        reassurance being sold to you — a wooden handle — does not address the
        part that touches your teeth. Both of those are worth an article
        because both are being repeated by people with something to sell.
      </p>

      <h2>What the 2025 study actually did</h2>
      <p>
        The paper is Wang and colleagues at Jilin University, published in{' '}
        <em>Ecotoxicology and Environmental Safety</em> in September 2025. They
        surveyed oral-care product use in China, then characterised the
        microplastics generated by the toothpastes and toothbrushes that survey
        identified as commonly used, and tracked how release from the brushes
        changed over time.
      </p>
      <p>Three findings matter, and only one of them travels:</p>
      <ul>
        <li>
          <strong>The toothpaste particles were polyethylene and
          polyethylene-vinyl acetate</strong> — 22% and 78% respectively,
          translucent and irregularly fragmented.
        </li>
        <li>
          <strong>The toothbrush simulation found polypropylene at the highest
          share, 22%</strong>, with <strong>63% of particles below
          100&nbsp;µm</strong>. This is the detail that gets lost: the coverage
          calls it a nylon-bristle finding, and the paper does not.
        </li>
        <li>
          <strong>Release from hard-bristled brushes rose quickly at first, then
          levelled off.</strong> New brushes shed fastest.
        </li>
      </ul>

      <h3>The sentence nobody quotes</h3>
      <p>
        Then the authors write this:{' '}
        <strong>&ldquo;The results suggest that the use of popular Chinese
        toothbrushes and toothpastes does not directly contribute to the health
        risks associated with exposure to MPs.&rdquo;</strong>
      </p>
      <p>
        And their closing line puts the numbers where they belong:{' '}
        <em>&ldquo;Continuous daily use of toothpaste and toothbrushes releases
        large numbers of MPs into the environment.&rdquo;</em>
      </p>
      <p>
        So the study measured an <strong>environmental</strong> load, assessed
        the <strong>personal</strong> risk, and found the personal risk not to be
        direct. The figure of 23.3 × 10⁵ particles per person per year — about
        2.33 million — is real, and it is the number that became &ldquo;2.3
        million particles straight into your mouth&rdquo; in retail copy for
        boar-bristle brushes. The study does not say that.
      </p>
      <p>
        Two further limits worth holding. These were{' '}
        <strong>Chinese products and a Chinese population</strong>, so the
        polymer mix does not automatically describe a brush bought in the US.
        And the other numbers circulating — &ldquo;39 particles every time you
        brush&rdquo;, &ldquo;30 to 120 per brushing&rdquo; — appear nowhere in
        the paper. We could not source them to any primary study.
      </p>

      <h2>The bamboo argument, and why the debunk is also wrong</h2>
      <p>
        The counter-genre is now as loud as the original: posts arguing that
        bamboo toothbrushes are a scam because the bristles are petroleum-based
        nylon regardless of the handle.
      </p>
      <p>
        For at least one major manufacturer that is simply not true.{' '}
        <strong>Brush with Bamboo&rsquo;s own FAQ states its bristles are
        &ldquo;100% Castor Bean Oil and USDA Certified 100% Biobased&rdquo;,
        containing &ldquo;0% fossil fuel and 0% petroleum&rdquo;.</strong>{' '}
        That is the manufacturer&rsquo;s published claim about its own feedstock,
        and there is no reason to reject it.
      </p>
      <p>
        The mistake is assuming that settles the question. It answers where the
        carbon came from, not what the material is.
      </p>

      <h3>What castor bristles actually are</h3>
      <p>
        Castor-derived bristles are <strong>nylon-11</strong> — polyamide 11,
        polymerised from 11-aminoundecanoic acid derived from castor beans, sold
        commercially by Arkema as Rilsan — or the related{' '}
        <strong>PA1010</strong>, built from two castor-derived monomers. Both are
        genuine members of the nylon family. They are made from a plant and they
        are still polyamides.
      </p>
      <p>
        And critically: <strong>neither nylon-11 nor PA1010 is
        home-compostable, and neither meaningfully biodegrades in an ordinary
        environment.</strong> Bio-based is a statement about the raw material
        entering the process. Biodegradable is a statement about what happens
        when you are finished with it. They are unrelated properties that share a
        prefix.
      </p>
      <p>
        This is the same distinction we draw about PLA in{' '}
        <Link href="/blog/is-it-plastic" className="text-forest underline">which household materials are actually plastic</Link>:
        plant-based and compostable describe origin and disposal, not behaviour
        in use. A castor-bristle bamboo brush is a real improvement on fossil
        feedstock and on a plastic handle. It is not a plastic-free object.
      </p>

      <BlogComparisonTable
        caption="What is actually on the end of your toothbrush (checked August 2026)"
        columns={[
          { key: 'b', label: 'Bristle type' },
          { key: 'w', label: 'What it is' },
          { key: 'd', label: 'Biodegrades?' },
        ]}
        rows={[
          { b: 'Conventional nylon', w: 'Petroleum-derived polyamide (nylon-6 or 6/6)', d: 'No' },
          { b: 'Polypropylene', w: 'The leading polymer in the 2025 study’s brushes', d: 'No' },
          { b: 'Castor / “plant-based” nylon', w: 'Nylon-11 (PA11) or PA1010 — polyamide from castor beans', d: 'No — bio-based, not biodegradable' },
          { b: 'Boar bristle', w: 'Animal keratin, not a polymer at all', d: 'Yes' },
          { b: 'Bamboo handle', w: 'Genuinely plant material — but it is the handle, not the bristle', d: 'Yes (handle only)' },
        ]}
      />

      <BlogAppCTA
        headline="Not sure what the plastic in your bathroom actually is?"
        sub="Scan a toothpaste tube, a floss box or a brush package and get the polymer, a 0–100 risk score, and a lower-plastic swap in about five seconds."
      />

      <h2>What we still do not know</h2>
      <ul>
        <li>
          <strong>Nobody has measured shedding from a castor/PA11 bristle
          specifically.</strong> The 2025 work characterised conventional
          brushes. Whether a bio-based polyamide abrades more, less or the same
          is an open question, and assuming it behaves like polypropylene would
          be a guess.
        </li>
        <li>
          <strong>No study has followed particles from brushing into a
          person.</strong> Release into a rinse basin is not the same
          measurement as uptake, which is exactly why the authors were careful
          about the health claim.
        </li>
        <li>
          <strong>Electric brush heads are unstudied here</strong>, despite
          higher speed and more contact cycles per session.
        </li>
        <li>
          <strong>The US polymer mix is unknown.</strong> A survey of Chinese
          products tells you what those brushes are made of, not what is in a
          multipack bought in Ohio.
        </li>
      </ul>

      <h2>What to actually do</h2>
      <p>
        The mechanism is abrasion, so every useful lever acts on abrasion — and
        the dental advice and the plastics advice point the same way, which is
        rare and worth taking:
      </p>
      <ul>
        <li>
          <strong>Use soft bristles and less pressure.</strong> The study found
          hard bristles shedding fastest. Dentists have argued for soft bristles
          for decades on enamel and gum-recession grounds. Both reasons agree.
        </li>
        <li>
          <strong>Replace on schedule, but do not replace early.</strong> Release
          was highest when brushes were new and tapered with use, so churning
          through brushes faster is not the win it sounds like. Roughly three
          months, or when the bristles splay.
        </li>
        <li>
          <strong>Rinse and flick the brush out rather than leaving it wet.</strong>{' '}
          Softened, waterlogged filaments abrade more readily.
        </li>
        <li>
          <strong>If you want the plastic genuinely gone, boar bristle is the
          only mainstream option</strong> — animal keratin, no polymer. It is
          not vegan, it wears differently, and it is a preference rather than a
          medical necessity.
        </li>
        <li>
          <strong>A bamboo handle is still worth buying</strong> if the reason is
          fossil feedstock and plastic waste. Just do not buy it believing the
          bristles are plant fibre.
        </li>
        <li>
          <strong>What not to do:</strong> switch on the strength of &ldquo;2.3
          million particles into your mouth&rdquo;. That framing is not what the
          study found, and a purchase made on a misquote tends not to survive
          contact with the correction.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">toothpaste without microplastics</Link>,{' '}
        <Link href="/blog/pfas-in-dental-floss" className="text-forest underline">PFAS in dental floss</Link>,{' '}
        <Link href="/blog/do-clear-aligners-release-microplastics" className="text-forest underline">clear aligners and microplastics</Link>, and{' '}
        <Link href="/blog/is-it-plastic" className="text-forest underline">which household materials are actually plastic</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do toothbrush bristles release microplastics?',
            answer: 'Yes. Bristles are polymer filaments abraded against teeth twice a day, and abrasion is what makes plastics shed. A 2025 study from Jilin University measured release from commonly used Chinese toothbrushes and reported an average of at least 23.3 × 10⁵ particles per person per year — about 2.33 million — with 63% of particles under 100 micrometres. The leading polymer they found was polypropylene at 22%.',
          },
          {
            question: 'Is it true that you swallow 2.3 million microplastics a year from your toothbrush?',
            answer: 'No, that is a misquote. The 2.33 million figure is real and comes from Wang et al. 2025, but the paper frames it as release into the environment, not ingestion, and states explicitly that the products tested "do not directly contribute to the health risks associated with exposure to MPs". The "into your mouth" framing was added by retailers selling alternative brushes. Other figures in circulation — 39 particles per brushing, or 30 to 120 — do not appear in the study at all.',
          },
          {
            question: 'Are bamboo toothbrush bristles made of plastic?',
            answer: 'They are made of nylon, but not necessarily petroleum nylon. Brush with Bamboo states in its own FAQ that its bristles are 100% castor bean oil, USDA-certified 100% biobased, containing 0% fossil fuel and 0% petroleum. Castor-derived bristles are nylon-11 (PA11, sold as Rilsan) or PA1010 — real polyamides made from a plant. So the common claim that bamboo brushes hide petroleum nylon is not universally accurate, and the claim that they are plastic-free is not accurate either.',
          },
          {
            question: 'Do plant-based or castor oil bristles biodegrade?',
            answer: 'No, and this is the most common confusion. Bio-based describes the raw material going into the polymer; biodegradable describes what happens to the finished product afterwards. They are unrelated properties. Neither nylon-11 nor PA1010 is home-compostable, and neither meaningfully biodegrades in an ordinary environment. A castor-bristle brush removes fossil feedstock from the supply chain; it does not leave a compostable object behind.',
          },
          {
            question: 'What is the only truly plastic-free toothbrush?',
            answer: 'A wooden or bamboo handle with boar bristle, because boar bristle is animal keratin rather than any polymer, and it genuinely biodegrades. It is not vegan, it softens differently when wet, and many people dislike it. Nothing in the current evidence makes it a health necessity — the study most often used to argue for it concluded there was no direct health risk from conventional brushes.',
          },
          {
            question: 'Do hard or soft bristles shed more?',
            answer: 'Hard bristles shed faster, particularly when the brush is new. The 2025 study tracked release over time and found the rate from hard-bristled brushes rising rapidly at first and then tapering with continued use. Soft bristles used with light pressure are also the long-standing dental recommendation for protecting enamel and preventing gum recession, so the plastics argument and the dental argument point the same direction here.',
          },
          {
            question: 'Should I replace my toothbrush more often to avoid microplastics?',
            answer: 'No — replacing early is counterproductive on this specific measure. Release was highest when brushes were new and declined as they were used, so shortening the replacement cycle means spending more time in the high-shedding phase. The standard guidance of roughly three months, or whenever the bristles splay, remains right; it is set by cleaning effectiveness and hygiene, which are better reasons than particle counts.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Wang S, Zheng N, Peng L, An Q, Chen C, Wei Y', year: 2025, title: 'Patterns and risks of microplastic release during primary oral care in Chinese residents — toothbrush release averaging at least 23.3 × 10⁵ particles/person/year and toothpaste up to 11.83 × 10⁵; polypropylene the leading toothbrush polymer at 22%; 63% of particles below 100 µm; authors conclude the products "do not directly contribute to the health risks associated with exposure to MPs"', publisher: 'Ecotoxicology and Environmental Safety 302:118694', url: 'https://pubmed.ncbi.nlm.nih.gov/40680448/' },
          { authors: 'Brush with Bamboo', year: 2026, title: 'Frequently asked questions — "Our toothbrush bristles are 100% Castor Bean Oil and USDA Certified 100% Biobased… our bristles contain 0% fossil fuel and 0% petroleum"', publisher: 'Brush with Bamboo (manufacturer)', url: 'https://brushwithbamboo.com/faq/' },
          { authors: 'Arkema', year: 2026, title: 'Rilsan polyamide 11 — bio-based polyamide polymerised from 11-aminoundecanoic acid derived from castor beans, the polymer used for castor-oil toothbrush bristles', publisher: 'Arkema (manufacturer)', url: 'https://www.arkema.com/global/en/products/product-finder/range/rilsan-polyamide-11/' },
        ]}
      />
    </BlogPostLayout>
  )
}
